import "./sidebar.css";
import {
    LineStyle,
    Timeline,
    PermIdentity,
    Storefront,
    AttachMoney,
    Add,
    StoreOutlined,
    QueryBuilder,
    Flag,
    ShoppingCart,
    AspectRatio,
    Code,
    ArrowBack,
    Folder,
    TrendingUp,
    Settings,
    Group,
    Anchor,
    EmojiEvents,
    Lock,
    ExitToApp,
    ShoppingCartOutlined,
    Payment,
    MoneyOff,
    Description,
    Menu as MenuIcon,
    Close as CloseIcon,
} from "@material-ui/icons";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { performLogout } from "../../redux/apiCalls";
import { useState, useEffect, useCallback } from "react";
import { userRequest } from "../../requestMethods";
import StepLabelsHelpToggle from "./StepLabelsHelpToggle";
import StepCodeTag from "../shared/StepCodeTag";
import { SIDEBAR_STEP_CODES } from "../../constants/workflowStepCodes";

const SidebarMenuLabel = ({ label, code, codes = [], showMenuText, compactTags = false }) => {
    if (!showMenuText) return null;

    const tagCodes = code ? [code] : codes;
    const tagSize = compactTags ? "xs" : "sm";

    return (
        <span className="sidebarMenuLabel">
            <span className="sidebarMenuLabelText">{label}</span>
            {tagCodes.length > 0 && (
                <span
                    className={`sidebarMenuLabelTags${
                        compactTags ? " sidebarMenuLabelTags--compact" : ""
                    }`}
                >
                    {tagCodes.map((tagCode) => (
                        <StepCodeTag key={tagCode} code={tagCode} size={tagSize} />
                    ))}
                </span>
            )}
        </span>
    );
};

export default function Sidebar() {
    const dispatch = useDispatch();
    const [isSmallScreen, setIsSmallScreen] = useState(
        window.innerWidth < 1367
    );
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [pendingSkip50Count, setPendingSkip50Count] = useState(0);

    // Initialize based on screen size and localStorage (only for desktop)
    const [isCollapsed, setIsCollapsed] = useState(() => {
        const isSmall = window.innerWidth < 1367;
        if (isSmall) {
            // Always start collapsed on small screens
            return true;
        }
        // On desktop, use localStorage
        const savedState = localStorage.getItem("sidebarCollapsed");
        return savedState === "true";
    });
    const location = useLocation();

    // Monitor screen size changes
    useEffect(() => {
        const handleResize = () => {
            const isSmall = window.innerWidth < 1367;
            const mobile = window.innerWidth <= 768;
            setIsSmallScreen(isSmall);
            setIsMobile(mobile);
            if (!mobile) setMobileMenuOpen(false);

            // Auto-collapse on small screens
            if (isSmall) {
                setIsCollapsed(true);
            } else {
                // On desktop, restore from localStorage
                const savedState = localStorage.getItem("sidebarCollapsed");
                setIsCollapsed(savedState === "true");
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Save collapse state to localStorage only for desktop screens
    useEffect(() => {
        if (!isSmallScreen) {
            localStorage.setItem("sidebarCollapsed", isCollapsed.toString());
        }
    }, [isCollapsed, isSmallScreen]);

    // Auto-collapse sidebar on smaller screens when navigating to a new page; close mobile menu on navigate
    useEffect(() => {
        if (isSmallScreen && !isCollapsed) {
            setIsCollapsed(true);
        }
        if (isMobile) {
            setMobileMenuOpen(false);
        }
    }, [location.pathname, isSmallScreen, isMobile]);

    // Close mobile menu on Escape
    useEffect(() => {
        if (!mobileMenuOpen || !isMobile) return;
        const handleEscape = (e) => {
            if (e.key === "Escape") setMobileMenuOpen(false);
        };
        window.addEventListener("keydown", handleEscape);
        return () => window.removeEventListener("keydown", handleEscape);
    }, [mobileMenuOpen, isMobile]);

    const superadmin = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.isSuperAdmin === true
            : null
    );

    const admin = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.isAdmin : null
    );

    const outright = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "outright"
            : null
    );

    const marketing = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "marketer"
            : null
    );

    const accountant = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "accountant"
            : null
    );

    const artist = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "drafter"
            : null
    );

    const purchaser = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "purchaser"
            : null
    );

    const production = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "production"
            : null
    );

    const productionManager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "productionManager"
            : null
    );

    const installer = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "installer"
            : null
    );

    const installerManager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "installerManager"
            : null
    );

    const regularUser = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.type === "user" : null
    );

    const manager = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.type === "manager"
            : null
    );

    const staff = useSelector((state) =>
        state.user.currentUser ? state.user.currentUser.type === "staff" : null
    );

    const hq = useSelector((state) =>
        state.user.currentUser
            ? state.user.currentUser.email === "admin@ssrmotorsport.com"
            : null
    );

    const currentUser = useSelector((state) => state.user.currentUser);
    const canSeeSkip50Requests =
        superadmin ||
        outright ||
        currentUser?._id === "693a87b313df78d75f75c484";

    const fetchPendingSkip50Count = useCallback(async () => {
        if (!canSeeSkip50Requests || !currentUser?._id) {
            setPendingSkip50Count(0);
            return;
        }

        try {
            let url = "/skip50Requests?status=pending";

            if (!superadmin) {
                const principleParam =
                    outright || manager
                        ? currentUser?._id
                        : currentUser?.principle;
                if (principleParam) {
                    url += `&principle=${principleParam}`;
                }
            }

            const res = await userRequest.get(url);
            const pending = Array.isArray(res.data) ? res.data.length : 0;
            setPendingSkip50Count(pending);
        } catch (err) {
            console.error("Error fetching pending skip 50 requests count:", err);
            setPendingSkip50Count(0);
        }
    }, [
        canSeeSkip50Requests,
        currentUser?._id,
        currentUser?.principle,
        superadmin,
        outright,
        manager,
    ]);

    useEffect(() => {
        fetchPendingSkip50Count();
    }, [fetchPendingSkip50Count]);

    useEffect(() => {
        const handleSkip50Changed = () => {
            fetchPendingSkip50Count();
        };

        window.addEventListener("skip50RequestsUpdated", handleSkip50Changed);
        return () => {
            window.removeEventListener(
                "skip50RequestsUpdated",
                handleSkip50Changed
            );
        };
    }, [fetchPendingSkip50Count]);

    const handleClick = () => {
        performLogout(dispatch);
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    // Labels when expanded, or when the mobile drawer is open.
    const showMenuText = !isCollapsed || (isMobile && mobileMenuOpen);
    const showSectionTitles = showMenuText;
    const isCollapsedLayout =
        isCollapsed && !(isMobile && mobileMenuOpen);

    if (!hq) {
        return (
            <div
                className={`sidebar ${isCollapsedLayout ? "collapsed" : ""} ${
                    isMobile ? "mobile" : ""
                } ${mobileMenuOpen ? "mobileMenuOpen" : ""}`}
            >
                <div className="sidebarWrapper">
                    {/* Header */}
                    <div className="sidebarHeader">
                        <div className="sidebarLogo flex justify-center w-full">
                            {!isCollapsed && (
                                <div className="flex justify-center">
                                    <Link
                                        to="/"
                                        style={{ display: "inline-block" }}
                                    >
                                        <img
                                            src={
                                                process.env.REACT_APP_PROJECT ===
                                                "JINSORA"
                                                    ? "https://ik.imagekit.io/dnddecpho/Jinsora/jinsora-logo_sO90GIVIc.jpeg"
                                                    : "https://ik.imagekit.io/dnddecpho/IHGroup/inhome-logo_ohlG8QLO0.png?updatedAt=1754324519910"
                                            }
                                            alt="InHome Logo"
                                            style={{
                                                width: "100%",
                                                maxWidth: "100px",
                                                height: "auto",
                                                cursor: "pointer",
                                            }}
                                        />
                                    </Link>
                                </div>
                            )}
                        </div>
                        <button
                            className="sidebarCollapseBtn"
                            onClick={toggleSidebar}
                        >
                            <ArrowBack
                                className={isCollapsed ? "rotated" : ""}
                            />
                        </button>
                    </div>

                    {/* Mobile logo (hidden on desktop) */}
                    <div className="sidebarMobileLogo">
                        <Link to="/" style={{ display: "inline-block" }}>
                            <img
                                src={
                                    process.env.REACT_APP_PROJECT === "JINSORA"
                                        ? "https://ik.imagekit.io/dnddecpho/Jinsora/jinsora-logo_sO90GIVIc.jpeg"
                                        : "https://ik.imagekit.io/dnddecpho/IHGroup/inhome-logo_ohlG8QLO0.png?updatedAt=1754324519910"
                                }
                                alt="InHome Logo"
                                style={{ height: "32px", width: "auto" }}
                            />
                        </Link>
                    </div>

                    {/* User Info */}
                    <div className="sidebarUser">
                        <div className="sidebarUserAvatar">
                            {currentUser?.name?.charAt(0) || "N"}
                        </div>
                        {!isCollapsed && (
                            <div className="sidebarUserInfo">
                                <div className="sidebarUserName">
                                    {currentUser?.name || "Newfin Consultancy"}
                                </div>
                                <div className="sidebarUserRole">
                                    {currentUser?.type
                                        ? currentUser.type
                                              .charAt(0)
                                              .toUpperCase() +
                                          currentUser.type.slice(1)
                                        : "User"}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Mobile menu trigger - only visible on mobile */}
                    {isMobile && (
                        <button
                            type="button"
                            className="sidebarMobileMenuBtn"
                            onClick={() => setMobileMenuOpen((o) => !o)}
                            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                        >
                            {mobileMenuOpen ? (
                                <CloseIcon />
                            ) : (
                                <MenuIcon />
                            )}
                        </button>
                    )}

                    <div className="sidebarMenuContent">
                    <div className="sidebarMenu">
                        <ul className="sidebarList">
                            <StepLabelsHelpToggle showMenuText={showMenuText} />
                        </ul>
                    </div>
                    {!marketing && (
                        <div className="sidebarMenu">
                            {showSectionTitles && (
                                <h3 className="sidebarTitle">Main Menu</h3>
                            )}
                            <ul className="sidebarList">
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname === "/"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Folder className="sidebarIcon" />
                                        <SidebarMenuLabel
                                            label="Orders"
                                            codes={SIDEBAR_STEP_CODES.orders}
                                            showMenuText={showMenuText}
                                            compactTags
                                        />
                                    </li>
                                </Link>
                                {canSeeSkip50Requests && (
                                    <>
                                        {(superadmin || outright) &&
                                            currentUser?.type !== "salesman" &&
                                            currentUser?._id !==
                                                "693a87b313df78d75f75c484" && (
                                                <Link
                                                    style={{
                                                        textDecoration: "none",
                                                        color: "inherit",
                                                    }}
                                                    to="/installmentRequests"
                                                    className="link"
                                                >
                                                    <li
                                                        className={`sidebarListItem ${
                                                            location.pathname ===
                                                            "/installmentRequests"
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                    >
                                                        <Payment className="sidebarIcon" />
                                                        {showMenuText &&
                                                            "Installment Requests"}
                                                    </li>
                                                </Link>
                                            )}
                                        <Link
                                            style={{
                                                textDecoration: "none",
                                                color: "inherit",
                                            }}
                                        to="/skip50Requests"
                                        className="link"
                                        >
                                            <li
                                                className={`sidebarListItem ${
                                                    location.pathname ===
                                                    "/skip50Requests"
                                                        ? "active"
                                                        : ""
                                                } ${
                                                    pendingSkip50Count > 0
                                                        ? "has-notification"
                                                        : ""
                                                }`}
                                            >
                                                <MoneyOff className="sidebarIcon" />
                                                {showMenuText && "Skip 50% Requests"}
                                                {pendingSkip50Count > 0 && (
                                                    <span className="sidebar-notification-badge">
                                                        {pendingSkip50Count > 99
                                                            ? "99+"
                                                            : pendingSkip50Count}
                                                    </span>
                                                )}
                                            </li>
                                        </Link>
                                    </>
                                )}
                                {(outright || manager) && (
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                        to="/logs"
                                        className="link"
                                    >
                                        <li
                                            className={`sidebarListItem ${
                                                location.pathname === "/logs"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <Description className="sidebarIcon" />
                                            {showMenuText && "Logs"}
                                        </li>
                                    </Link>
                                )}
                                {!accountant &&
                                    !artist &&
                                    !purchaser &&
                                    !production &&
                                    !productionManager &&
                                    !installer &&
                                    !installerManager &&
                                    !regularUser && (
                                        <>
                                            <Link
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                                to="/pos"
                                                className="link"
                                            >
                                                <li
                                                    className={`sidebarListItem ${
                                                        location.pathname ===
                                                        "/pos"
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <ShoppingCartOutlined className="sidebarIcon" />
                                                    <SidebarMenuLabel
                                                        label="POS"
                                                        code={SIDEBAR_STEP_CODES.pos}
                                                        showMenuText={showMenuText}
                                                    />
                                                </li>
                                            </Link>
                                            {(superadmin ||
                                                manager ||
                                                outright ||
                                                marketing) && (
                                                <Link
                                                    style={{
                                                        textDecoration: "none",
                                                        color: "inherit",
                                                    }}
                                                    to="/warrantyrequests"
                                                    className="link"
                                                >
                                                    <li
                                                        className={`sidebarListItem ${
                                                            location.pathname ===
                                                            "/warrantyrequests"
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                    >
                                                        <Settings className="sidebarIcon" />
                                                        {showMenuText &&
                                                            "Warranty Requests"}
                                                    </li>
                                                </Link>
                                            )}
                                            <Link
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                                to="/history"
                                                className="link"
                                            >
                                                <li
                                                    className={`sidebarListItem ${
                                                        location.pathname ===
                                                        "/history"
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <Settings className="sidebarIcon" />
                                                    {showMenuText &&
                                                        "Customer History"}
                                                </li>
                                            </Link>
                                        </>
                                    )}
                            </ul>
                        </div>
                    )}

                    {!artist &&
                        !purchaser &&
                        !production &&
                        !productionManager &&
                        !installer &&
                        !installerManager &&
                        !regularUser && (
                            <div className="sidebarMenu">
                                {showSectionTitles && (
                                    <h3 className="sidebarTitle">Management</h3>
                                )}
                                <ul className="sidebarList">
                                    {(superadmin ||
                                        manager ||
                                        outright ||
                                        marketing ||
                                        staff) && (
                                        <>
                                            <Link
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                                to="/users"
                                                className="link"
                                            >
                                                <li
                                                    className={`sidebarListItem ${
                                                        location.pathname ===
                                                        "/users"
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <Group className="sidebarIcon" />
                                                    {showMenuText && "Users"}
                                                </li>
                                            </Link>
                                        </>
                                    )}
                                    {!accountant && !installerManager && (
                                        <>
                                            <Link
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                                to="/newuser"
                                                className="link"
                                            >
                                                <li
                                                    className={`sidebarListItem ${
                                                        location.pathname ===
                                                        "/newuser"
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <Add className="sidebarIcon" />
                                                    <SidebarMenuLabel
                                                        label="New User"
                                                        code={SIDEBAR_STEP_CODES.newUser}
                                                        showMenuText={showMenuText}
                                                    />
                                                </li>
                                            </Link>
                                            {(superadmin || outright || manager) && (
                                                <Link
                                                    style={{
                                                        textDecoration: "none",
                                                        color: "inherit",
                                                    }}
                                                    to="/newstaff"
                                                    className="link"
                                                >
                                                    <li
                                                        className={`sidebarListItem ${
                                                            location.pathname ===
                                                            "/newstaff"
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                    >
                                                        <Add className="sidebarIcon" />
                                                        {showMenuText &&
                                                            "New Staff"}
                                                    </li>
                                                </Link>
                                            )}
                                            {(superadmin ||
                                                manager ||
                                                outright) && (
                                                <Link
                                                    style={{
                                                        textDecoration: "none",
                                                        color: "inherit",
                                                    }}
                                                    to="/admins"
                                                    className="link"
                                                >
                                                    <li
                                                        className={`sidebarListItem ${
                                                            location.pathname ===
                                                            "/admins"
                                                                ? "active"
                                                                : ""
                                                        }`}
                                                    >
                                                        <Lock className="sidebarIcon" />
                                                        {showMenuText &&
                                                            "Admins"}
                                                    </li>
                                                </Link>
                                            )}
                                        </>
                                    )}
                                </ul>
                            </div>
                        )}

                    {/* Journal Menu */}
                    {(superadmin ||
                        outright ||
                        installerManager ||
                        installer ||
                        staff ||
                        productionManager) && (
                        <div className="sidebarMenu">
                            {showSectionTitles && (
                                <h3 className="sidebarTitle">Journal</h3>
                            )}
                            <ul className="sidebarList">
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/journals"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname === "/journals"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Folder className="sidebarIcon" />
                                        {showMenuText && "Journals"}
                                    </li>
                                </Link>
                                {(installerManager ||
                                    productionManager) && (
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                        to="/newJournal"
                                        className="link"
                                    >
                                        <li
                                            className={`sidebarListItem ${
                                                location.pathname ===
                                                "/journals/new"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <Add className="sidebarIcon" />
                                            {showMenuText && "New Journal"}
                                        </li>
                                    </Link>
                                )}
                            </ul>
                        </div>
                    )}

                    {(superadmin || outright || purchaser || (productionManager && process.env.REACT_APP_PROJECT === "JINSORA")) && (
                        <div className="sidebarMenu">
                            {showSectionTitles && (
                                <h3 className="sidebarTitle">Purchase Order</h3>
                            )}
                            <ul className="sidebarList">
                                {!accountant && (
                                    <>
                                        <Link
                                            style={{
                                                textDecoration: "none",
                                                color: "inherit",
                                            }}
                                            to="/companies"
                                            className="link"
                                        >
                                            <li
                                                className={`sidebarListItem ${
                                                    location.pathname ===
                                                    "/companies"
                                                        ? "active"
                                                        : ""
                                                }`}
                                            >
                                                <Group className="sidebarIcon" />
                                                {showMenuText && "Companies"}
                                            </li>
                                        </Link>
                                        <Link
                                            style={{
                                                textDecoration: "none",
                                                color: "inherit",
                                            }}
                                            to="/newCompany"
                                            className="link"
                                        >
                                            <li
                                                className={`sidebarListItem ${
                                                    location.pathname ===
                                                    "/newcompany"
                                                        ? "active"
                                                        : ""
                                                }`}
                                            >
                                                <Add className="sidebarIcon" />
                                                {showMenuText && "New Company"}
                                            </li>
                                        </Link>
                                    </>
                                )}
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/purchaseOrders"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname ===
                                            "/purchaseOrders"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Folder className="sidebarIcon" />
                                        {showMenuText && "Purchase Orders"}
                                    </li>
                                </Link>
                            </ul>
                        </div>
                    )}

                    {superadmin && (
                        <div className="sidebarMenu">
                            {showSectionTitles && (
                                <h3 className="sidebarTitle">Products</h3>
                            )}
                            <ul className="sidebarList">
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/products"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname === "/products"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Storefront className="sidebarIcon" />
                                        {showMenuText && "Products"}
                                    </li>
                                </Link>
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/newproduct"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname === "/newproduct"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Add className="sidebarIcon" />
                                        {showMenuText && "New Product"}
                                    </li>
                                </Link>
                            </ul>
                        </div>
                    )}

                    {(superadmin || outright || purchaser) && (
                        <div className="sidebarMenu">
                            {showSectionTitles && (
                                <h3 className="sidebarTitle">Materials</h3>
                            )}
                            <ul className="sidebarList">
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/materials"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname === "/materials"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Storefront className="sidebarIcon" />
                                        {showMenuText && "Materials"}
                                    </li>
                                </Link>
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/newmaterial"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname === "/newmaterial"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Add className="sidebarIcon" />
                                        {showMenuText && "New Material"}
                                    </li>
                                </Link>
                            </ul>
                        </div>
                    )}

                    {(superadmin || outright) &&
                        !artist &&
                        !purchaser &&
                        !production &&
                        !productionManager &&
                        !installer &&
                        !regularUser && (
                            <div className="sidebarMenu">
                                {showSectionTitles && (
                                    <h3 className="sidebarTitle">
                                        Reports
                                    </h3>
                                )}
                                <ul className="sidebarList">
                                    <Link
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                                to="/salesreport"
                                                className="link"
                                            >
                                                <li
                                                    className={`sidebarListItem ${
                                                        location.pathname ===
                                                        "/salesreport"
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <Flag className="sidebarIcon" />
                                                    {showMenuText &&
                                                        "Sales Report"}
                                                </li>
                                            </Link>
                                            <Link
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                                to="/orderinsights"
                                                className="link"
                                            >
                                                <li
                                                    className={`sidebarListItem ${
                                                        location.pathname ===
                                                        "/orderinsights"
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <QueryBuilder className="sidebarIcon" />
                                                    {showMenuText &&
                                                        "Order Insights"}
                                                </li>
                                            </Link>
                                            <Link
                                                style={{
                                                    textDecoration: "none",
                                                    color: "inherit",
                                                }}
                                                to="/report"
                                                className="link"
                                            >
                                                <li
                                                    className={`sidebarListItem ${
                                                        location.pathname ===
                                                        "/report"
                                                            ? "active"
                                                            : ""
                                                    }`}
                                                >
                                                    <TrendingUp className="sidebarIcon" />
                                                    {showMenuText &&
                                                        "Performance Report"}
                                                </li>
                                    </Link>
                                </ul>
                            </div>
                        )}

                    {/* Settings at bottom */}
                    <div
                        className="logoutContainer"
                        style={{ marginTop: "auto", paddingTop: "24px" }}
                    >
                        <div className="sidebarMenu">
                            <ul className="sidebarList">
                                <Link to="/changepassword" className="link">
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname ===
                                            "/changepassword"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Lock className="sidebarIcon" />
                                        {showMenuText && "Change Password"}
                                    </li>
                                </Link>
                                <li
                                    className="sidebarListItem"
                                    onClick={handleClick}
                                >
                                    <ExitToApp className="sidebarIcon" />
                                    {showMenuText && "Logout"}
                                </li>
                            </ul>
                        </div>
                    </div>
                    </div>
                </div>
                {/* Mobile menu backdrop - close on click */}
                {isMobile && mobileMenuOpen && (
                    <div
                        className="sidebarMobileMenuBackdrop"
                        onClick={() => setMobileMenuOpen(false)}
                        onKeyDown={(e) => e.key === "Escape" && setMobileMenuOpen(false)}
                        role="button"
                        tabIndex={0}
                        aria-label="Close menu"
                    />
                )}
            </div>
        );
    }

    return (
        hq && (
            <div className={`sidebar ${isCollapsed ? "collapsed" : ""}`}>
                <div className="sidebarWrapper">
                    {/* Header */}
                    <div className="sidebarHeader">
                        <div className="sidebarLogo">
                            {!isCollapsed && (
                                <Link
                                    to="/"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    <div style={{ cursor: "pointer" }}>
                                        <div className="sidebarLogoText">
                                            NFC
                                        </div>
                                        <div className="sidebarLogoSubtext">
                                            NEWFIN CONSULTANCY
                                        </div>
                                    </div>
                                </Link>
                            )}
                        </div>
                        <button
                            className="sidebarCollapseBtn"
                            onClick={toggleSidebar}
                        >
                            <ArrowBack
                                className={isCollapsed ? "rotated" : ""}
                            />
                        </button>
                    </div>

                    {/* User Info */}
                    <div className="sidebarUser">
                        <div className="sidebarUserAvatar">
                            {currentUser?.name?.charAt(0) || "N"}
                        </div>
                        {!isCollapsed && (
                            <div className="sidebarUserInfo">
                                <div className="sidebarUserName">
                                    {currentUser?.name || "Newfin Consultancy"}
                                </div>
                                <div className="sidebarUserRole">
                                    {currentUser?.type
                                        ? currentUser.type
                                              .charAt(0)
                                              .toUpperCase() +
                                          currentUser.type.slice(1)
                                        : "User"}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="sidebarMenu">
                        <ul className="sidebarList">
                            <StepLabelsHelpToggle showMenuText={showMenuText} />
                        </ul>
                    </div>

                    <div className="sidebarMenu">
                        {!isCollapsed && (
                            <h3 className="sidebarTitle">Dashboard</h3>
                        )}
                        <ul className="sidebarList">
                            <Link
                                style={{
                                    textDecoration: "none",
                                    color: "inherit",
                                }}
                                to="/"
                                className="link"
                            >
                                <li
                                    className={`sidebarListItem ${
                                        location.pathname === "/"
                                            ? "active"
                                            : ""
                                    }`}
                                >
                                    <LineStyle className="sidebarIcon" />
                                    {showMenuText && "Home"}
                                </li>
                            </Link>
                        </ul>
                    </div>
                    <div className="sidebarMenu">
                        {!isCollapsed && (
                            <h3 className="sidebarTitle">Users</h3>
                        )}
                        <ul className="sidebarList">
                            {(superadmin ||
                                manager ||
                                outright ||
                                marketing ||
                                staff) && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                        to="/users"
                                        className="link"
                                    >
                                        <li
                                            className={`sidebarListItem ${
                                                location.pathname === "/users"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <PermIdentity className="sidebarIcon" />
                                            {showMenuText && "Users"}
                                        </li>
                                    </Link>
                                </>
                            )}
                            {!accountant && !installerManager && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                        to="/newuser"
                                        className="link"
                                    >
                                        <li
                                            className={`sidebarListItem ${
                                                location.pathname === "/newuser"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <Add className="sidebarIcon" />
                                            <SidebarMenuLabel
                                                label="New User"
                                                code={SIDEBAR_STEP_CODES.newUser}
                                                showMenuText={showMenuText}
                                            />
                                        </li>
                                    </Link>
                                </>
                            )}
                        </ul>
                    </div>

                    <div className="sidebarMenu">
                        {!isCollapsed && (
                            <h3 className="sidebarTitle">Purchase Order</h3>
                        )}
                        <ul className="sidebarList">
                            {!accountant && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                        to="/companies"
                                        className="link"
                                    >
                                        <li
                                            className={`sidebarListItem ${
                                                location.pathname ===
                                                "/companies"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <PermIdentity className="sidebarIcon" />
                                            {showMenuText && "Companies"}
                                        </li>
                                    </Link>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                        to="/newCompany"
                                        className="link"
                                    >
                                        <li
                                            className={`sidebarListItem ${
                                                location.pathname ===
                                                "/newcompany"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <Add className="sidebarIcon" />
                                            {showMenuText && "New Company"}
                                        </li>
                                    </Link>
                                </>
                            )}
                        </ul>
                    </div>

                    <div className="sidebarMenu">
                        {(superadmin ||
                            manager ||
                            outright ||
                            admin ||
                            marketing) &&
                            !isCollapsed && (
                                <h3 className="sidebarTitle">Products</h3>
                            )}
                        <ul className="sidebarList">
                            {(superadmin ||
                                manager ||
                                outright ||
                                admin ||
                                marketing) && (
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/products"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname === "/products"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Storefront className="sidebarIcon" />
                                        {showMenuText && "Products"}
                                    </li>
                                </Link>
                            )}
                            {(superadmin ||
                                manager ||
                                outright ||
                                marketing) && (
                                <>
                                    <Link
                                        style={{
                                            textDecoration: "none",
                                            color: "inherit",
                                        }}
                                        to="/newproduct"
                                        className="link"
                                    >
                                        <li
                                            className={`sidebarListItem ${
                                                location.pathname ===
                                                "/newproduct"
                                                    ? "active"
                                                    : ""
                                            }`}
                                        >
                                            <Add className="sidebarIcon" />
                                            {showMenuText && "New Product"}
                                        </li>
                                    </Link>
                                </>
                            )}
                        </ul>
                    </div>

                    {/* Materials Section */}
                    {superadmin && (
                        <div className="sidebarMenu">
                            {showSectionTitles && (
                                <h3 className="sidebarTitle">Materials</h3>
                            )}
                            <ul className="sidebarList">
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/materials"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname ===
                                            "/materials"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Storefront className="sidebarIcon" />
                                        {showMenuText && "Materials"}
                                    </li>
                                </Link>
                                <Link
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                    to="/newmaterial"
                                    className="link"
                                >
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname ===
                                            "/newmaterial"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Add className="sidebarIcon" />
                                        {showMenuText && "New Material"}
                                    </li>
                                </Link>
                            </ul>
                        </div>
                    )}

                    {/* Settings at bottom */}
                    <div
                        className="logoutContainer"
                        style={{ marginTop: "auto", paddingTop: "24px" }}
                    >
                        <div className="sidebarMenu">
                            <ul className="sidebarList">
                                <Link to="/changepassword" className="link">
                                    <li
                                        className={`sidebarListItem ${
                                            location.pathname ===
                                            "/changepassword"
                                                ? "active"
                                                : ""
                                        }`}
                                    >
                                        <Lock className="sidebarIcon" />
                                        {showMenuText && "Change Password"}
                                    </li>
                                </Link>
                                <li
                                    className="sidebarListItem"
                                    onClick={handleClick}
                                >
                                    <ExitToApp className="sidebarIcon" />
                                    {showMenuText && "Logout"}
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
        )
    );
}
